/* eslint-disable max-lines */
import React, { FC, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import { Column } from '../../../../../../components/grid/Grid';
import { PopoverFactoryRenderer } from '../../../../../../components/popovers/PopoverFactoryRenderer';
import { RestrictLinks } from '../../components/restrict-links/RestrictLinks';
import { PopoverSelectionFromDataAttribute } from '../../../../../../components/popovers/PopoverSelectionFromDataAttribute';
import { ExternalLink } from '../../components/external-link/ExternalLink';
import { LinkedLinks } from '../../components/linked-links/LinkedLinks';
import { CopyLegislationButton } from '../../components/copy-legislation-button/CopyLegislationButton';
import { TextLoader } from '../../components/text-loader/TextLoader';
import { LinkedTextsModal } from '../../components/linked-texts-modal/LinkedTextsModal';

import {
  LegislationInfos,
  LinkedArticleType,
} from '../../../../legislation.types';

import { useSearchParamsModal } from '../../../../../../components/modal/useSearchParamsModal';
import { getFormattedDate } from '../../../../../../utils/date-utils/dateUtils';
import { linkTypes } from '../../utils/utils';

import styles from './text.module.scss';
import { doctrineAnalytics } from '../../../../../../utils/analyticsUtils';

type Props = {
  legislationInfos: LegislationInfos;
  linkedLegislation: LinkedArticleType | null;
  articleId: string;
  articleTitle: string;
  initialVersionId: string;
  textNature: string;
  fakeLoader: boolean;
  quotedByLegislations: LinkedArticleType | null;
};

export const Text: FC<Props> = ({
  legislationInfos,
  linkedLegislation,
  articleId,
  initialVersionId,
  textNature,
  articleTitle,
  fakeLoader,
  quotedByLegislations,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const quotedByLegislationsCount = quotedByLegislations?.quotedBy?.count ?? 0;

  const { openModal } = useSearchParamsModal('linked-text', (modalProps) => (
    <LinkedTextsModal
      articleId={articleId}
      articleTitle={articleTitle}
      articlesLength={quotedByLegislationsCount}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...modalProps}
    />
  ));

  const onClickLinkedTextSeeMore = () => {
    doctrineAnalytics('Legislation MoreLinkedLegislationClicked', {
      article_version_id: articleId,
      article_id: initialVersionId,
      legislationType: textNature,
    });
    openModal();
  };

  const displayPopover = (element: HTMLElement) => {
    const source = 'legislation_popover';
    if (element) {
      return (
        <PopoverSelectionFromDataAttribute element={element} source={source} />
      );
    }
  };

  if (fakeLoader) {
    return (
      <Column>
        <TextLoader />
      </Column>
    );
  }

  return (
    <>
      <Column medium={8} className={styles.textContainer}>
        <div className={styles.textContent}>
          <>
            {!legislationInfos.rawContent.length ? (
              <FormattedMessage id="page/sections/Text.tsx/5" />
            ) : (
              <>
                <PopoverFactoryRenderer renderTemplate={displayPopover}>
                  <div
                    ref={contentRef}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: legislationInfos?.content,
                    }}
                  />
                </PopoverFactoryRenderer>
                {contentRef && contentRef.current && (
                  <>
                    <RestrictLinks container={contentRef.current} />
                  </>
                )}
              </>
            )}
          </>
        </div>
      </Column>
      <Column medium={4} className={styles.metadataContainer}>
        {legislationInfos?.startingDate && (
          <div className={classNames(styles.metadataItem)}>
            <p>
              <FormattedMessage
                id="page/sections/Text.tsx/2"
                values={{
                  date: getFormattedDate(legislationInfos?.startingDate),
                }}
              />
              {linkTypes.start.map(({ id, labelId, source }) => (
                <LinkedLinks
                  linkedLegislation={linkedLegislation}
                  labelId={labelId}
                  id={id}
                  key={id}
                  source={source}
                />
              ))}
            </p>
          </div>
        )}
        {legislationInfos?.endingDate && (
          <div className={classNames(styles.metadataItem)}>
            <p>
              <FormattedMessage
                id="page/sections/Text.tsx/3"
                values={{
                  date: getFormattedDate(legislationInfos?.endingDate),
                }}
              />
              {linkTypes.end.map(({ id, labelId, source }) => (
                <LinkedLinks
                  linkedLegislation={linkedLegislation}
                  labelId={labelId}
                  id={id}
                  key={id}
                  source={source}
                />
              ))}
            </p>
          </div>
        )}
        {!!linkedLegislation?.newTexts.items.length && (
          <div className={classNames(styles.metadataItem)}>
            <p>
              <FormattedMessage id="page/sections/Text.tsx/4" />
              {linkTypes.new.map(({ id, source }) => (
                <LinkedLinks
                  linkedLegislation={linkedLegislation}
                  id={id}
                  key={id}
                  source={source}
                />
              ))}
            </p>
          </div>
        )}
        {!!linkedLegislation?.oldTexts.items.length && (
          <div className={classNames(styles.metadataItem)}>
            <p>
              <FormattedMessage id="page/sections/Text.tsx/6" />
              {linkTypes.old.map(({ id, source }) => (
                <LinkedLinks
                  linkedLegislation={linkedLegislation}
                  id={id}
                  key={id}
                  source={source}
                />
              ))}
            </p>
          </div>
        )}
        {quotedByLegislationsCount > 0 && (
          <div className={classNames(styles.metadataItem)}>
            <span
              className={styles.quotedByLegislationsLink}
              role="button"
              tabIndex={0}
              onClick={onClickLinkedTextSeeMore}
            >
              <FormattedMessage
                id="page/sections/Text.tsx/7"
                values={{ count: quotedByLegislationsCount }}
              />
            </span>
          </div>
        )}
        {legislationInfos && (
          <>
            <hr className={styles.separator} />
            <CopyLegislationButton legislationInfos={legislationInfos} />
            {legislationInfos.externalUrl && (
              <ExternalLink
                articleId={articleId}
                url={legislationInfos.externalUrl.url}
              />
            )}
          </>
        )}
      </Column>
    </>
  );
};
